import type { BattleMove, BattleState, BattleEffect } from '../types/battle';
import { calculateItemEffect } from './itemEffects';

const CRITICAL_HIT_CHANCE = 0.15;
const CRITICAL_HIT_MULTIPLIER = 1.5;
const BASE_DAMAGE_PERCENT = 0.15;
const MAX_HEALTH = 100;
const DEFEND_HEAL_AMOUNT = 15;

export function calculateMoveEffects(move: BattleMove, state: BattleState): BattleEffect[] {
  const effects: BattleEffect[] = [];
  const isPlayerMove = state.currentTurn === 'player';
  const attacker = isPlayerMove ? state.playerPets[0] : state.aiPets[0];
  const attackerStats = attacker?.stats || { strength: 10, agility: 10, intelligence: 10 };

  // Handle item usage first
  if (move.item) {
    if (move.item.quantity <= 0) {
      return [{
        type: 'heal',
        value: 0,
        description: 'No items remaining!'
      }];
    }
    const itemEffect = calculateItemEffect(move.item);
    // Decrease item quantity
    move.item.quantity--;
    return [itemEffect];
  }

  switch (move.type) {
    case 'attack': {
      const baseDamage = Math.floor(MAX_HEALTH * BASE_DAMAGE_PERCENT);
      const isCritical = Math.random() < CRITICAL_HIT_CHANCE;
      const strengthModifier = 1 + (attackerStats.strength / 100);
      const finalDamage = Math.max(1, Math.floor(
        baseDamage * strengthModifier * (isCritical ? CRITICAL_HIT_MULTIPLIER : 1)
      ));
      
      effects.push({
        type: 'damage',
        value: finalDamage,
        isCritical,
        description: `${isPlayerMove ? 'You deal' : 'Enemy deals'} ${finalDamage} damage${isCritical ? ' (Critical Hit!)' : ''}`
      });
      break;
    }

    case 'defend': {
      // Add shield effect
      const baseShield = Math.floor(MAX_HEALTH * 0.2);
      const agilityModifier = 1 + (attackerStats.agility / 100);
      const finalShield = Math.floor(baseShield * agilityModifier);

      effects.push({
        type: 'shield',
        value: finalShield,
        duration: 2,
        description: `${isPlayerMove ? 'You gain' : 'Enemy gains'} ${finalShield} shield`
      });

      // Add healing effect - heal the one who defends
      effects.push({
        type: 'heal',
        value: DEFEND_HEAL_AMOUNT,
        description: `${isPlayerMove ? 'You heal' : 'Enemy heals'} for ${DEFEND_HEAL_AMOUNT} HP`,
        target: isPlayerMove ? 'player' : 'ai' // Specify the target explicitly
      });
      break;
    }

    case 'special': {
      const specialDamage = Math.floor(MAX_HEALTH * 0.25);
      const intelligenceModifier = 1 + (attackerStats.intelligence / 100);
      const finalDamage = Math.floor(specialDamage * intelligenceModifier);

      effects.push({
        type: 'damage',
        value: finalDamage,
        isSpecial: true,
        description: `${isPlayerMove ? 'Your' : 'Enemy'} special attack deals ${finalDamage} damage!`
      });
      break;
    }
  }

  return effects;
}

export function applyEffects(state: BattleState, effects: BattleEffect[], target: 'player' | 'ai'): BattleState {
  const newState = { ...state };

  effects.forEach(effect => {
    switch (effect.type) {
      case 'damage':
        if (target === 'player') {
          newState.playerHealth = Math.max(0, newState.playerHealth - effect.value);
        } else {
          newState.aiHealth = Math.max(0, newState.aiHealth - effect.value);
        }
        break;

      case 'heal':
        // Use the explicit target if provided, otherwise heal the opposite of the damage target
        const healTarget = effect.target || (target === 'player' ? 'ai' : 'player');
        if (healTarget === 'player') {
          newState.playerHealth = Math.min(MAX_HEALTH, newState.playerHealth + effect.value);
        } else {
          newState.aiHealth = Math.min(MAX_HEALTH, newState.aiHealth + effect.value);
        }
        break;

      case 'shield':
        const targetEffects = target === 'player' ? newState.activeEffects.player : newState.activeEffects.ai;
        targetEffects.push({
          type: 'shield',
          value: effect.value,
          duration: effect.duration || 2,
          description: effect.description
        });
        break;
    }
  });

  return newState;
}

export function isGameOver(state: BattleState): boolean {
  return state.playerHealth <= 0 || state.aiHealth <= 0;
}

export function getWinner(state: BattleState): 'player' | 'ai' | null {
  if (!isGameOver(state)) return null;
  return state.playerHealth <= 0 ? 'ai' : 'player';
}